import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";
import { useB2BTranslation } from "../../B2BShared/hooks/useB2BTranslation";
import { useB2BFormLoadingState } from "../../B2BShared/hooks/useB2BLoadingState";
import { useB2BAuth } from "../../B2BShared/B2BAuthContext/B2BAuthContext";
import "./B2BLoginForm.css";

const B2BLoginForm = () => {
  const { t, tAuth, i18n } = useB2BTranslation();
  const { isAuthenticated, login } = useB2BAuth();
  const navigate = useNavigate();
  
  const {
    isLoading,
    error,
    handleSubmit: handleFormSubmit,
    setError
  } = useB2BFormLoadingState();
  
  const [, setLanguage] = useState(i18n.language);
  const [showPassword, setShowPassword] = useState(false);
  
  // Initialize React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError: setFieldError,
    setValue,
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
    mode: "onChange",
  });

  // Load remembered credentials on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("b2bRememberedEmail");
    const rememberedPassword = localStorage.getItem("b2bRememberedPassword");
    
    if (rememberedEmail) {
      setValue("email", rememberedEmail);
      setValue("rememberMe", true);
      
      if (rememberedPassword) {
        setValue("password", rememberedPassword);
      }
    }
  }, [setValue]);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setLanguage(lng);
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  // Form is now handled by useB2BFormField hook

  const onSubmit = async (values: any) => {
    // Note: Auto-logout is handled in the auth context

    // Show loading message only for auto-logout
    if (isAuthenticated) {
      setError(tAuth("switchingUsers"));
    }

    try {
      await handleFormSubmit(async () => {
        // Use the auth context login function
        await login(values.email, values.password, values.rememberMe);

        // Handle "Remember Me" functionality - store or clear credentials
        if (values.rememberMe) {
          // Store email and password in localStorage
          localStorage.setItem("b2bRememberedEmail", values.email);
          localStorage.setItem("b2bRememberedPassword", values.password);
        } else {
          // Clear stored credentials if rememberMe is unchecked
          localStorage.removeItem("b2bRememberedEmail");
          localStorage.removeItem("b2bRememberedPassword");
        }

        // Wait a bit longer to ensure the auth context and localStorage are updated
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Get user data from localStorage (should be updated by login function)
        const userData = JSON.parse(localStorage.getItem("b2bUser") || "{}");

        if (!userData || !userData.email) {
          throw new Error("Login successful but user data not found. Please try again.");
        }

        // Step 1: Check email verification
        if (!userData.isEmailVerified) {
          navigate("/gcb/verify-email", { state: { email: userData.email } });
          return;
        }

        // Step 2: Check profile completion (after email verification)
        if (!userData.profileComplete) {
          navigate("/gcb/profile/create");
          return;
        }

        // Step 3: Check approval status (only if profile is complete)
        if (!userData.isApproved && !userData.isAdmin) {
          navigate("/gcb/waiting-approval");
          return;
        }

        // Step 4: All checks passed - redirect to dashboard
        if (userData.isApproved || userData.isAdmin) {
          navigate("/gcb/dashboard");
          return;
        }

        // Fallback case
        navigate("/gcb/waiting-approval");
      });
    } catch (err: any) {
      // Handle pending approval case - check both error response and direct response
      const pendingApprovalCode =
        err.response?.data?.code === "PENDING_APPROVAL" ||
        err.code === "PENDING_APPROVAL" ||
        (err.response?.data && !err.response.data.success && err.response.data.code === "PENDING_APPROVAL");

      if (pendingApprovalCode) {
        // Store token and user data if available in error response
        const token = err.response?.data?.token || err.response?.data?.data?.token;
        const userData = err.response?.data?.user || err.response?.data?.data?.user;

        if (token) {
          localStorage.setItem("b2bToken", token);
        }
        if (userData) {
          localStorage.setItem("b2bUser", JSON.stringify(userData));
        }

        navigate("/gcb/waiting-approval");
        return; // handled
      }

      // Handle email not verified case
      const emailNotVerified = err.response?.data?.code === "EMAIL_NOT_VERIFIED" || err.code === "EMAIL_NOT_VERIFIED";

      if (emailNotVerified) {
        navigate("/gcb/verify-email", {
          state: { email: values.email },
        });
        return; // handled
      }

      // Show specific error messages based on the error
      if (err.response?.status === 401) {
        setError(tAuth("invalidEmailOrPassword"));
      } else if (err.response?.status === 429) {
        setError(tAuth("rateLimitError"));
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data?.fieldErrors) {
        Object.keys(err.response.data.fieldErrors).forEach((field) => {
          setFieldError(field as any, err.response.data.fieldErrors[field]);
        });
      } else if (err.message?.includes("401")) {
        setError(tAuth("authenticationFailed"));
      } else {
        setError(err.message || tAuth("loginFailed") || "Login failed. Please try again.");
      }
      // IMPORTANT: don't re-throw — prevents CRA uncaught runtime error overlay
    }
  };

  return (
    <div className="b2b-login-container">
      <div className="b2b-login-card">
        <div className="back-to-b2b-container">
          <Link
            to="/gcb"
            className="back-to-b2b-button"
          >
            {t("backToB2BPage") || "Back to main GCB Page"}
          </Link>
        </div>

        <h2>{t("welcomeBack") || "Welcome Back"}</h2>
        <p className="login-subtitle">{t("loginSubtitle") || "Sign in to your GCB account"}</p>

        {error && <div className="error-message">{error}</div>}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="login-form"
        >
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              {tAuth("email") || "Email"}
            </label>
            <input
              {...register("email", {
                required: t("validation.emailRequired") || "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t("validation.emailInvalid") || "Invalid email address"
                }
              })}
              type="email"
              id="email"
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder={tAuth("emailPlaceholder") || "Enter your email"}
            />
            {errors.email && (
              <span className="error-message">{errors.email.message}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              {tAuth("password") || "Password"}
            </label>
            <div className="password-input-container">
              <input
                {...register("password", {
                  required: t("validation.passwordRequired") || "Password is required",
                  minLength: {
                    value: 6,
                    message: t("validation.passwordMinLength") || "Password must be at least 6 characters"
                  }
                })}
                type={showPassword ? "text" : "password"}
                id="password"
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder={tAuth("passwordPlaceholder") || "Enter your password"}
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => !prev)}
                className="password-toggle"
                tabIndex={-1}
              >
                {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
            {errors.password && (
              <span className="error-message">{errors.password.message}</span>
            )}
          </div>

          <div className="form-options">
            <div className="form-checkbox">
              <input
                {...register("rememberMe")}
                type="checkbox"
                id="rememberMe"
                className="form-checkbox-input"
              />
              <label htmlFor="rememberMe" className="form-checkbox-label">
                {tAuth("rememberMe") || "Remember me"}
              </label>
            </div>
            <Link
              to="/gcb/forgot-password"
              className="forgot-password"
            >
              {t("forgotPassword") || "Forgot Password?"}
            </Link>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="login-button"
          >
            {isLoading ? (t("signingIn") || "Signing In...") : (t("signIn") || "Sign In")}
          </button>
        </form>

        <div className="register-link">
          {t("notMemberYet") || "Not a member yet?"}{" "}
          <Link to="/gcb/register">{t("signUpHere") || "Register here"}</Link>
        </div>
      </div>
    </div>
  );
};

export default B2BLoginForm;